export type AccessShareSocketData = {
  role: "anonymous" | "listener" | "requester";
  requestId: string | null;
};

export type PendingAccessRequest = {
  id: string;
  pairingCode: string;
  publicKey: string;
};

export type ClientToServerMessage =
  | { type: "listen" }
  | { type: "request"; publicKey: string }
  | {
      type: "approve";
      requestId: string;
      publicKeyA: string;
      encrypted: string;
      iv: string;
    }
  | { type: "deny"; requestId: string }
  | { type: "stop" };

export type ServerToClientMessage =
  | { type: "listening" }
  | { type: "pending"; requests: PendingAccessRequest[] }
  | { type: "request"; id: string; pairingCode: string; publicKey: string }
  | { type: "waiting"; requestId: string; pairingCode: string }
  | { type: "granted"; publicKeyA: string; encrypted: string; iv: string }
  | { type: "denied" }
  | { type: "resolved"; requestId: string }
  | { type: "error"; message: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parsePendingRequest(value: unknown): PendingAccessRequest | null {
  if (!isRecord(value)) return null;
  const id = asNonEmptyString(value.id);
  const pairingCode = asNonEmptyString(value.pairingCode);
  const publicKey = asNonEmptyString(value.publicKey);
  if (!id || !pairingCode || !publicKey) return null;
  return { id, pairingCode, publicKey };
}

export function parseClientMessage({
  raw,
}: {
  raw: string;
}): ClientToServerMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || typeof parsed.type !== "string") return null;

  if (parsed.type === "listen") return { type: "listen" };
  if (parsed.type === "stop") return { type: "stop" };

  if (parsed.type === "request") {
    const publicKey = asNonEmptyString(parsed.publicKey);
    if (!publicKey) return null;
    return { type: "request", publicKey };
  }

  if (parsed.type === "deny") {
    const requestId = asNonEmptyString(parsed.requestId);
    if (!requestId) return null;
    return { type: "deny", requestId };
  }

  if (parsed.type === "approve") {
    const requestId = asNonEmptyString(parsed.requestId);
    const publicKeyA = asNonEmptyString(parsed.publicKeyA);
    const encrypted = asNonEmptyString(parsed.encrypted);
    const iv = asNonEmptyString(parsed.iv);
    if (!requestId || !publicKeyA || !encrypted || !iv) return null;
    return { type: "approve", requestId, publicKeyA, encrypted, iv };
  }

  return null;
}

export function parseServerMessage({
  raw,
}: {
  raw: string;
}): ServerToClientMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || typeof parsed.type !== "string") return null;

  if (parsed.type === "listening") return { type: "listening" };
  if (parsed.type === "denied") return { type: "denied" };

  if (parsed.type === "pending") {
    if (!Array.isArray(parsed.requests)) return null;
    const requests: PendingAccessRequest[] = [];
    for (const item of parsed.requests) {
      const request = parsePendingRequest(item);
      if (!request) return null;
      requests.push(request);
    }
    return { type: "pending", requests };
  }

  if (parsed.type === "request") {
    const request = parsePendingRequest(parsed);
    if (!request) return null;
    return {
      type: "request",
      id: request.id,
      pairingCode: request.pairingCode,
      publicKey: request.publicKey,
    };
  }

  if (parsed.type === "waiting") {
    const requestId = asNonEmptyString(parsed.requestId);
    const pairingCode = asNonEmptyString(parsed.pairingCode);
    if (!requestId || !pairingCode) return null;
    return { type: "waiting", requestId, pairingCode };
  }

  if (parsed.type === "granted") {
    const publicKeyA = asNonEmptyString(parsed.publicKeyA);
    const encrypted = asNonEmptyString(parsed.encrypted);
    const iv = asNonEmptyString(parsed.iv);
    if (!publicKeyA || !encrypted || !iv) return null;
    return { type: "granted", publicKeyA, encrypted, iv };
  }

  if (parsed.type === "resolved") {
    const requestId = asNonEmptyString(parsed.requestId);
    if (!requestId) return null;
    return { type: "resolved", requestId };
  }

  if (parsed.type === "error") {
    const message = asNonEmptyString(parsed.message);
    if (!message) return null;
    return { type: "error", message };
  }

  return null;
}

export function encodeMessage({
  payload,
}: {
  payload: ClientToServerMessage | ServerToClientMessage;
}): string {
  return JSON.stringify(payload);
}
